/**
 * ONE form layout renderer, adopted by Console and Portal (#2009).
 *
 * ── the defect ────────────────────────────────────────────────────────────
 *
 * Console and Portal each owned a section grid. They disagreed in two ways a
 * person could see:
 *
 *   · Console honoured desktop and tablet spans through CSS classes and
 *     `@media`; Portal set `grid-template-columns` inline and applied
 *     `item.layoutSpan?.desktop` at EVERY width. A configured tablet span was
 *     simply absent from one of the two hosts.
 *   · Both asked the VIEWPORT. A record embedded in a narrow split pane inside
 *     a wide window therefore selected the wide layout, and an automotive
 *     960x480 surface selected a layout meant for a 960px page.
 *
 * This component owns the geometry and the semantics; the host owns the
 * controls. That division is why one renderer can serve an editable console
 * form with react-hook-form controls AND a customer-safe portal form with
 * read-only facts, without either host knowing how the other lays out.
 *
 * ── what it deliberately does NOT do ──────────────────────────────────────
 *
 * It does not clamp, normalize, or decide a profile. `applyFormLayout` already
 * finished the geometry: a v1 document is normalized to v2 on read, and a span
 * wider than its own profile's track never arrives because the VALIDATOR
 * REFUSES it — `span.<profile> > section.columns.<profile>` is not a document
 * that can be read at all. Nothing clamps, at either end, and an earlier draft
 * of this comment claimed the adapter did; `applyFormLayout`'s own header
 * records why the clamp it once had was unreachable code.
 *
 * A renderer that re-decided any of that would be the second opinion that let
 * the two hosts drift in the first place. The one `min()` in the stylesheet is
 * for a section built BY HAND that never passed a validator — a fixture, a
 * test, a future caller — and is documented there as such.
 *
 * It reads no user agent, no device and no window size. The profile is chosen
 * by `@container` against this component's own inline size, which is the only
 * measurement that answers "how much room does this form actually have".
 *
 * ── MUTATION TABLE (rule 10) — applied, restored, red read by name ────────
 *
 * | # | mutation                                                  | measured |
 * |---|-----------------------------------------------------------|----------|
 * | S | every cell emits its DESKTOP span on all three tracks — Portal's original behaviour, and the issue's "make Portal use desktop span" | **RED** — "the TABLET span did not reach the DOM — this is the value Portal used to ignore" |
 *
 * The fixture that reds it had to be fixed first: it gave its wide field a span
 * of `{desktop: 2, tablet: 2, phone: 1}`, and two equal numbers cannot show
 * that one was substituted for the other. Three different values now.
 */
import type { ReactNode } from 'react'
import type { LayoutProfile, ResponsiveColumns, ResponsiveSpanV2 } from '@solum/experience'
import s from './FormLayoutSections.module.css'
import e from '../shell/Engraved.module.css'

/**
 * The geometry types are the GRAMMAR's own, re-exported rather than restated.
 *
 * They were once a local `{ desktop: number; tablet: number; phone: number }`,
 * which is a different and looser contract: it admits 0, 7 and 2 on a phone
 * track that the grammar caps at 2, so a host could hand this component a
 * document the server would have refused and the types would agree. A finite
 * vocabulary that is finite in one package and open in the next is not finite.
 */
export type FormLayoutColumns = ResponsiveColumns
export type FormLayoutSpan = ResponsiveSpanV2
/** @deprecated the columns/span distinction is real; prefer the two above. */
export type FormLayoutTracks = ResponsiveColumns

export interface FormLayoutField {
  /** stable field key; also the React key and the cell's data attribute */
  field: string
  /** Never wider than its own profile's track: the validator refuses such a
   *  document, so one cannot reach a host. Absent means one column. */
  layoutSpan?: FormLayoutSpan
}

export interface FormLayoutSection<F extends FormLayoutField> {
  title: string
  description?: string
  layoutId?: string
  layoutColumns?: FormLayoutColumns
  fields: F[]
}

export interface FormLayoutSectionsProps<F extends FormLayoutField> {
  sections: FormLayoutSection<F>[]
  /** The host's control for one field. Console returns a governed input and its
   *  validation; Portal returns a customer-safe value or its own editor. */
  renderField: (field: F, section: FormLayoutSection<F>) => ReactNode
  /** Rendered inside each section's fieldset, after the grid. */
  renderSectionFooter?: (section: FormLayoutSection<F>) => ReactNode
  className?: string
  'data-testid'?: string
}

/** The default when a section carries no authored geometry at all. Two columns
 *  on a wide box, one on a phone — the shape both hosts already defaulted to. */
const FALLBACK: ResponsiveColumns = { desktop: 2, tablet: 2, phone: 1 }

/** Geometry as custom properties: all three profiles present, container picks. */
const trackVars = (
  tracks: Readonly<Record<LayoutProfile, number>> | undefined,
  prefix: string,
): Record<string, string> => {
  const value = tracks ?? FALLBACK
  return {
    [`--${prefix}-desktop`]: String(value.desktop),
    [`--${prefix}-tablet`]: String(value.tablet),
    [`--${prefix}-phone`]: String(value.phone),
  }
}

export function FormLayoutSections<F extends FormLayoutField>({
  sections, renderField, renderSectionFooter, className, ...rest
}: FormLayoutSectionsProps<F>) {
  return (
    <div className={className ? `${s.root} ${className}` : s.root}
      data-testid={rest['data-testid'] ?? 'form-layout-sections'}>
      {sections.map((section, index) => {
        const columns = section.layoutColumns ?? FALLBACK
        return (
          /* `fieldset`/`legend` in BOTH hosts. Portal previously used
             `section`/`h3`, which reads as a document heading rather than as a
             group of controls; a screen-reader user filling a portal form got
             no group name announced with the fields. The semantics are part of
             the shared contract, not a console preference. */
          <fieldset key={section.layoutId ?? `${section.title}.${index}`}
            className={s.section}
            data-section-id={section.layoutId ?? undefined}
            data-columns-desktop={columns.desktop}
            data-columns-tablet={columns.tablet}
            data-columns-phone={columns.phone}>
            <legend className={`${s.legend} ${e.label}`}>{section.title}</legend>
            {section.description && <p className={s.description}>{section.description}</p>}
            <div className={s.grid} data-testid="form-layout-grid"
              style={trackVars(section.layoutColumns, 'cols')}>
              {section.fields.map((field) => {
                const span = field.layoutSpan ?? { desktop: 1, tablet: 1, phone: 1 }
                return (
                  <div key={field.field} className={s.cell}
                    data-field={field.field}
                    data-span-desktop={span.desktop}
                    data-span-tablet={span.tablet}
                    data-span-phone={span.phone}
                    style={trackVars(field.layoutSpan ?? { desktop: 1, tablet: 1, phone: 1 }, 'span')}>
                    {renderField(field, section)}
                  </div>
                )
              })}
            </div>
            {renderSectionFooter?.(section)}
          </fieldset>
        )
      })}
    </div>
  )
}
